import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { fmtUSD } from "../lib/format";
import {
  ChevronRight,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const monthLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

function DeltaBadge({ value, invert }) {
  if (value == null)
    return (
      <span className="inline-flex items-center text-[10.5px] text-zinc-400 font-mono">
        <Minus size={9} strokeWidth={2} className="mr-0.5" />—
      </span>
    );
  // For "total_out" we treat down as good → invert sign coloring
  const positive = invert ? value < 0 : value >= 0;
  const tone = positive
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : "text-rose-700 bg-rose-50 border-rose-200";
  const Icon = value === 0 ? Minus : value >= 0 ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center text-[10.5px] font-mono px-1 py-0.5 rounded border ${tone}`}
    >
      <Icon size={9} strokeWidth={2} className="mr-0.5" />
      {value >= 0 ? "+" : ""}
      {value}%
    </span>
  );
}

export default function TeamAnalysis() {
  const [teams, setTeams] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const r = await api.get("/team-analysis");
      setTeams(r.data.teams || []);
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (name) => {
    setLoading(true);
    try {
      const r = await api.get(
        `/team-analysis/${encodeURIComponent(name)}`
      );
      setDetail(r.data);
      setSelected(name);
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  if (selected && detail) {
    const t = detail.totals || {};
    return (
      <Layout
        title={`Team · ${detail.team_name}`}
        subtitle="Monthly performance, margins and trends"
        testId="team-detail-page"
        actions={
          <button
            onClick={() => {
              setSelected(null);
              setDetail(null);
              loadOverview();
            }}
            className="h-9 inline-flex items-center px-3 rounded-md border hairline bg-white hover:bg-zinc-50 text-[13px]"
            data-testid="back-to-teams-btn"
          >
            <ArrowLeft size={14} strokeWidth={1.7} className="mr-1.5" />
            All teams
          </button>
        }
      >
        {/* Lifetime totals */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          <div className="stat-card p-4">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Months tracked
            </div>
            <div className="font-mono text-[22px] font-medium mt-1">
              {detail.month_count}
            </div>
          </div>
          <div className="stat-card p-4">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Lifetime in
            </div>
            <div className="font-mono text-[22px] font-medium mt-1 text-emerald-700">
              {fmtUSD(t.total_in)}
            </div>
          </div>
          <div className="stat-card p-4">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Lifetime out
            </div>
            <div className="font-mono text-[22px] font-medium mt-1 text-rose-700">
              {fmtUSD(t.total_out)}
            </div>
          </div>
          <div className="stat-card p-4">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Lifetime net
            </div>
            <div className="font-mono text-[22px] font-medium mt-1 text-emerald-700">
              {fmtUSD(t.net_profit)}
            </div>
            <div className="text-[10.5px] text-zinc-500 mt-0.5">
              Gross {fmtUSD(t.gross_profit)}
            </div>
          </div>
          <div className="stat-card p-4">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Avg profit margin
            </div>
            <div
              className={`font-mono text-[22px] font-medium mt-1 ${
                t.avg_profit_margin_pct >= 0
                  ? "text-emerald-700"
                  : "text-rose-700"
              }`}
            >
              {t.avg_profit_margin_pct}%
            </div>
            <div className="text-[10.5px] text-zinc-500 mt-0.5">
              Net ÷ In
            </div>
          </div>
        </div>

        {/* Monthly table */}
        <div className="stat-card overflow-hidden">
          <div className="p-4 border-b hairline">
            <div className="font-display font-bold tracking-tight">
              Monthly history
            </div>
            <div className="text-[11.5px] text-zinc-500 mt-0.5">
              MoM deltas vs the previous month
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                  <th className="py-3 px-4 font-medium">Month</th>
                  <th className="py-3 px-4 font-medium text-right">In</th>
                  <th className="py-3 px-4 font-medium text-right">Out</th>
                  <th className="py-3 px-4 font-medium text-right">In-Out %</th>
                  <th className="py-3 px-4 font-medium text-right">Gross</th>
                  <th className="py-3 px-4 font-medium text-right">
                    Coin cut
                  </th>
                  <th className="py-3 px-4 font-medium text-right">
                    Coin / Gross
                  </th>
                  <th className="py-3 px-4 font-medium text-right">Net</th>
                  <th className="py-3 px-4 font-medium text-right">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y hairline">
                {detail.months.map((m) => (
                  <tr
                    key={m.month}
                    className="row-hover"
                    data-testid={`team-month-${m.month}`}
                  >
                    <td className="py-3 px-4 font-medium">
                      {monthLabel(m.month)}
                    </td>
                    <td className="py-3 px-4 font-mono text-right">
                      <div>{fmtUSD(m.total_in)}</div>
                      <div className="mt-0.5">
                        <DeltaBadge value={m.delta?.total_in} />
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-right">
                      <div className="text-rose-700">{fmtUSD(m.total_out)}</div>
                      <div className="mt-0.5">
                        <DeltaBadge value={m.delta?.total_out} invert />
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-right">
                      <div>{m.in_out_margin_pct}%</div>
                      <div className="mt-0.5">
                        <DeltaBadge value={m.delta?.in_out_margin_pct} />
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-right">
                      <div>{fmtUSD(m.gross_profit)}</div>
                      <div className="mt-0.5">
                        <DeltaBadge value={m.delta?.gross_profit} />
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-right text-amber-700">
                      {fmtUSD(m.coin_cut_usd)}
                      <div className="text-[10.5px] text-zinc-500">
                        raw{" "}
                        {Number(m.coin_used_raw).toLocaleString("en-US", {
                          maximumFractionDigits: 4,
                        })}
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-right text-zinc-600">
                      {m.coin_pct_of_gross}%
                    </td>
                    <td className="py-3 px-4 font-mono text-right font-semibold text-emerald-700">
                      <div>{fmtUSD(m.net_profit)}</div>
                      <div className="mt-0.5 font-normal">
                        <DeltaBadge value={m.delta?.net_profit} />
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-right">
                      <div
                        className={
                          m.profit_margin_pct >= 0
                            ? "text-emerald-700"
                            : "text-rose-700"
                        }
                      >
                        {m.profit_margin_pct}%
                      </div>
                      <div className="mt-0.5">
                        <DeltaBadge value={m.delta?.profit_margin_pct} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  // Overview list
  return (
    <Layout
      title="Team Analysis"
      subtitle="Per-team monthly performance & trend tracking"
      testId="team-analysis-page"
    >
      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : teams.length === 0 ? (
        <div className="stat-card p-10 flex flex-col items-center text-center">
          <BarChart3
            size={22}
            strokeWidth={1.5}
            className="text-zinc-400 mb-2"
          />
          <div className="text-[14px] font-medium">No teams yet</div>
          <div className="text-[12.5px] text-zinc-500 mt-1 max-w-sm">
            Add team sheets in <strong>EM Salary Sheets</strong> first — they
            will show up here automatically.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((t) => {
            const positive = (t.latest_margin_pct || 0) >= 0;
            return (
              <button
                key={t.team_name}
                onClick={() => loadDetail(t.team_name)}
                className="stat-card p-5 text-left hover:shadow-md hover:border-zinc-300 transition-shadow"
                data-testid={`team-card-${t.team_name}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display text-[15px] font-bold tracking-tight">
                    {t.team_name}
                  </div>
                  <ChevronRight
                    size={14}
                    strokeWidth={1.7}
                    className="text-zinc-400"
                  />
                </div>
                <div className="text-[11.5px] text-zinc-500 mt-1">
                  {t.month_count} month{t.month_count === 1 ? "" : "s"} ·{" "}
                  {monthLabel(t.first_month)} → {monthLabel(t.latest_month)}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-[12px]">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
                      Latest in
                    </div>
                    <div className="font-mono text-[14px] mt-0.5">
                      {fmtUSD(t.latest_in)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
                      Latest out
                    </div>
                    <div className="font-mono text-[14px] mt-0.5 text-rose-700">
                      {fmtUSD(t.latest_out)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
                      Latest gross
                    </div>
                    <div className="font-mono text-[14px] mt-0.5">
                      {fmtUSD(t.latest_gross)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
                      Latest net
                    </div>
                    <div className="font-mono text-[14px] mt-0.5 text-emerald-700">
                      {fmtUSD(t.latest_net)}
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t hairline flex items-center justify-between">
                  <span className="text-[10.5px] uppercase tracking-widest text-zinc-500">
                    Latest margin
                  </span>
                  <span
                    className={`font-mono text-[16px] font-medium ${
                      positive ? "text-emerald-700" : "text-rose-700"
                    }`}
                  >
                    {t.latest_margin_pct}%
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
